import { isNil, isNotNil } from "@etudescarre/utils";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Navigate, useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { authClient } from "@/lib/auth-client";
import { homePathFor, isStaffRole } from "@/lib/home-path";
import {
  YARD_ACCESS_DENIED_TITLE,
  yardOpenFailureMessage,
} from "@/lib/yard-access";

type YardData = {
  id: number;
  name: string;
  type: "lots_separes" | "entreprise_generale" | "conception_realisation";
  comissioningDate: string | null;
  expirationDate: string | null;
  beforeArchivingDays: number | null;
  isLocked: boolean;
  moaCompanyName: string | null;
};

type EmployeeData = {
  id: string;
  firstName: string | null;
  lastName: string | null;
};

type ContractData = {
  id: number;
  type: string;
  isActive: boolean;
  code: string | null;
  performerCompanyName: string;
  function: string | null;
  isReferent: boolean;
  teams: Array<{
    id: number;
    name: string;
    isMember: boolean;
  }>;
};

type YardContextValue = {
  yard: YardData;
  employee: EmployeeData;
  contracts: ContractData[];
  hasContractType: (type: string) => boolean;
  primaryContract: ContractData;
};

const YardContext = createContext<YardContextValue | null>(null);

type YardProviderProps = {
  children: ReactNode;
};

type FetchState =
  | { status: "loading" }
  // The yard cannot be shown: the account goes back to its home page and a
  // toast says why, instead of an "access denied" page.
  | { status: "left" }
  | {
      status: "success";
      data: {
        yard: YardData;
        employee: EmployeeData;
        contracts: ContractData[];
      };
    };

export function YardProvider({ children }: YardProviderProps) {
  const { yardId } = useParams<{ yardId: string }>();
  const session = authClient.useSession();
  const role = session.data?.user.role;
  const impersonated = isNotNil(session.data?.session.impersonatedBy);
  const staff = isStaffRole(role) && !impersonated;
  const [state, setState] = useState<FetchState>({ status: "loading" });

  useEffect(() => {
    if (isNil(yardId)) {
      setState({ status: "left" });
      return;
    }

    const currentYardId = yardId;
    let cancelled = false;

    async function fetchContext() {
      setState({ status: "loading" });

      const { data, error, response } = await apiClient.GET(
        "/api/yards/{yardId}/context",
        { params: { path: { yardId: currentYardId } } },
      );

      if (cancelled) return;

      if (isNotNil(error) || isNil(data)) {
        // A 401 means the session ended: the login screen takes over.
        if (response.status !== 401) {
          toaster.error({
            title: YARD_ACCESS_DENIED_TITLE,
            description: yardOpenFailureMessage(response.status, error, staff),
          });
        }
        setState({ status: "left" });
        return;
      }

      setState({ status: "success", data: data.data });
    }

    fetchContext();
    return () => {
      cancelled = true;
    };
  }, [yardId, staff]);

  if (state.status === "loading") {
    return (
      <div className="flex min-h-full items-center justify-center">
        <div className="text-zinc-500">Chargement du chantier...</div>
      </div>
    );
  }

  if (state.status === "left") {
    return <Navigate to={homePathFor(role, impersonated)} replace />;
  }

  const { yard, employee, contracts } = state.data;

  const hasContractType = (type: string) =>
    contracts.some((c) => c.type === type);

  const value: YardContextValue = {
    yard,
    employee,
    contracts,
    hasContractType,
    primaryContract: contracts[0],
  };

  return <YardContext.Provider value={value}>{children}</YardContext.Provider>;
}

export function useYardContext() {
  const context = useContext(YardContext);
  if (isNil(context)) {
    throw new Error("useYardContext must be used within a YardProvider");
  }
  return context;
}
